/**
 * Catálogo de status da OS.
 *
 * Espelha a seed da tabela `os_status` (migration 005). O banco é a fonte de
 * verdade em runtime — o frontend consome `GET /api/v1/catalogos/status`.
 * Esta cópia existe para tipagem, cores de fallback e testes.
 *
 * Corrige #16 (branch morto de cor), #19 (select vazio com status desconhecido)
 * e #20 (ausência de constraint de status).
 */
export const CATEGORIAS_STATUS = [
    'aguardando',
    'andamento',
    'concluido',
    'pendente',
    'cancelado',
];
export const OS_STATUS = [
    { codigo: 'aguardando_orcamento', rotulo: 'Aguardando Orçamento', categoria: 'aguardando', cor: 'red', ordem: 1, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'orcamento_enviado', rotulo: 'Orçamento Enviado', categoria: 'andamento', cor: 'blue', ordem: 2, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'orcamento_aprovado', rotulo: 'Orçamento Aprovado', categoria: 'concluido', cor: 'green', ordem: 3, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'orcamento_recusado', rotulo: 'Orçamento Recusado', categoria: 'cancelado', cor: 'gray', ordem: 4, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'aguardando_peca', rotulo: 'Aguardando Peça', categoria: 'pendente', cor: 'yellow', ordem: 5, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'em_reparo', rotulo: 'Em Reparo', categoria: 'andamento', cor: 'blue', ordem: 6, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'reparo_concluido', rotulo: 'Reparo Concluído', categoria: 'concluido', cor: 'green', ordem: 7, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'pronto_retirada', rotulo: 'Pronto para Retirada', categoria: 'concluido', cor: 'green', ordem: 8, exigeSaida: false, exigePagamento: false, finalizaOs: false },
    { codigo: 'pago', rotulo: 'Pago', categoria: 'concluido', cor: 'green', ordem: 9, exigeSaida: false, exigePagamento: true, finalizaOs: false },
    { codigo: 'pago_parcial', rotulo: 'Pago Parcial', categoria: 'pendente', cor: 'yellow', ordem: 10, exigeSaida: false, exigePagamento: true, finalizaOs: false },
    { codigo: 'retirado', rotulo: 'Retirado', categoria: 'concluido', cor: 'green', ordem: 11, exigeSaida: true, exigePagamento: true, finalizaOs: true },
    { codigo: 'sem_reparo', rotulo: 'Sem Reparo', categoria: 'cancelado', cor: 'gray', ordem: 12, exigeSaida: true, exigePagamento: false, finalizaOs: true },
    { codigo: 'cancelado', rotulo: 'Cancelado', categoria: 'cancelado', cor: 'gray', ordem: 13, exigeSaida: false, exigePagamento: false, finalizaOs: true },
    { codigo: 'cancelado_retirado', rotulo: 'Cancelado & Retirado', categoria: 'cancelado', cor: 'amber', ordem: 14, exigeSaida: true, exigePagamento: false, finalizaOs: true },
    { codigo: 'descartado', rotulo: 'Descartado', categoria: 'cancelado', cor: 'gray', ordem: 15, exigeSaida: true, exigePagamento: false, finalizaOs: true },
    { codigo: 'cancelado_descartado', rotulo: 'Cancelado & Descartado', categoria: 'cancelado', cor: 'amber', ordem: 16, exigeSaida: true, exigePagamento: false, finalizaOs: true },
];
export const CODIGOS_STATUS = OS_STATUS.map((s) => s.codigo);
export const STATUS_INICIAL = 'aguardando_orcamento';
/**
 * Pseudo-status para linhas cujo `ordens_servico.status` está NULL ou tem um
 * texto fora do catálogo — o banco não tem constraint (defeito #20) e hoje
 * existem 10 OS com status NULL.
 *
 * Não é gravável: `CODIGOS_STATUS` não o inclui. Serve só para a OS aparecer
 * na tela em vez de sumir ou zerar o seletor (defeito #19).
 */
export const STATUS_INDEFINIDO = {
    codigo: 'indefinido',
    rotulo: 'Indefinido',
    categoria: 'aguardando',
    cor: 'red',
    ordem: 0,
    exigeSaida: false,
    exigePagamento: false,
    finalizaOs: false,
};
export function acharStatus(codigo) {
    if (!codigo)
        return STATUS_INDEFINIDO;
    return OS_STATUS.find((s) => s.codigo === codigo) ?? STATUS_INDEFINIDO;
}
/** Rótulo humano com fallback — nunca renderiza vazio (corrige #19). */
export function rotuloStatus(codigo) {
    return acharStatus(codigo).rotulo;
}
/** Normaliza texto para comparação: sem acento, sem caixa, sem espaço duplo. */
function normalizar(texto) {
    return texto
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Converte o texto gravado em `ordens_servico.status` no código canônico.
 *
 * O banco guarda o RÓTULO ('Aguardando Orçamento'), não o código. Esta é a
 * tradução na borda de leitura — o inverso de `rotuloStatus`, usado na escrita.
 * Retorna `null` quando o texto não casa com nenhum status conhecido.
 */
export function codigoPorRotulo(rotulo) {
    if (!rotulo)
        return null;
    const alvo = normalizar(rotulo);
    const achado = OS_STATUS.find((s) => normalizar(s.rotulo) === alvo);
    if (achado)
        return achado.codigo;
    // Tolera quem já tenha gravado o código em vez do rótulo.
    return OS_STATUS.find((s) => s.codigo === alvo.replace(/[^a-z]+/g, '_'))?.codigo ?? null;
}
/**
 * Classes Tailwind por categoria de status. Chaveado por categoria (e não por
 * `includes` em texto livre) — é isso que elimina o branch morto do
 * `cadcli.html`, onde 'cancelado & retirado' casava antes com 'retirado'.
 */
export const CLASSES_CATEGORIA = {
    aguardando: 'bg-red-100 text-red-800 ring-red-600/20',
    andamento: 'bg-blue-100 text-blue-800 ring-blue-600/20',
    concluido: 'bg-green-100 text-green-800 ring-green-600/20',
    pendente: 'bg-yellow-100 text-yellow-800 ring-yellow-600/20',
    cancelado: 'bg-slate-200 text-slate-700 ring-slate-500/20',
};
/** Cor sólida (bolinha do monitor). */
export const CORES_CATEGORIA = {
    aguardando: '#dc2626',
    andamento: '#3b82f6',
    concluido: '#16a34a',
    pendente: '#eab308',
    cancelado: '#6b7280',
};
export function classesBadgeStatus(codigo) {
    return CLASSES_CATEGORIA[acharStatus(codigo).categoria];
}
export function corStatus(codigo) {
    return CORES_CATEGORIA[acharStatus(codigo).categoria];
}
/**
 * Formas de pagamento — vocabulário canônico da API.
 *
 * O banco tem HOJE dois vocabulários divergentes para a mesma coisa:
 *   `pagamento.descricao` → 'PIX', 'Débito', 'Crédito', 'Dinheiro'
 *   `caixa.pagamento`     → 'PIX', 'Cartão de Débito', 'Cartão de Crédito (1x)'
 * A API expõe um só; a borda de escrita grava o dialeto de cada tabela e a de
 * leitura traduz os dois de volta (ver `backend/src/shared/formas-pagamento.ts`).
 */
export const FORMAS_PAGAMENTO = [
    'Dinheiro',
    'PIX',
    'Cartão de Débito',
    'Cartão de Crédito',
    'Transferência',
];
//# sourceMappingURL=status.js.map